"use client";
import { PhoneCall, X } from "lucide-react";

import OverlayWrapper from "@/components/ui/OverLayWrapper";
import {
  PlusSVG,
  HeadingSVG,
  ParagraphSVG,
  ImageSVG,
  DividerSVG,
  BulletSVG,
  FaqSVG,
  DoubleQuoteSVG,
  UrlSVG,
  VideoSVG,
  ReferenceSVG,
  ProductListSVG,
  TableSVG,
} from "@/components/ui/SVG";
import { AnimatePresence } from "framer-motion";
import type { BlockBase } from "@/cms/blocks/registry/types";
type IconComponent = React.ComponentType<any>;

export function BlockPickerOverlay({
  open,
  onClose,
  blockOptions,
  onAddBlock,
  canCloneBlocks,
  cloning,
  onOpenClone,
  onPasteBlock,
}: {
  open: boolean;
  onClose: () => void;
  blockOptions: Array<{ type: BlockBase["type"]; label: string }>;
  onAddBlock: (type: BlockBase["type"]) => void;
  canCloneBlocks: boolean;
  cloning: boolean;
  onOpenClone: () => void;
  onPasteBlock: () => void;
}) {
  const iconMap: Partial<Record<BlockBase["type"], IconComponent>> = {
    heading: HeadingSVG,
    paragraph: ParagraphSVG,
    image: ImageSVG,
    divider: DividerSVG,
    list: BulletSVG,
    faq: FaqSVG,
    quote: DoubleQuoteSVG,
    link: UrlSVG,
    embed: VideoSVG,
    contentReference: ReferenceSVG,
    reference: ReferenceSVG,
    productCard: ProductListSVG,
    table: TableSVG,
    contact: PhoneCall,
  };

  return (
    <AnimatePresence>
      {open && (
        <OverlayWrapper
          onClose={onClose}
          snapPoints={["70vh"]}
          className="bg-white"
        >
          <div className="mb-20 flex h-full flex-col">
            <div className="sticky top-0 z-30 bg-white px-4 pb-3">
              <div className="flex items-center justify-between text-sm font-semibold tracking-wide text-gray-600 uppercase">
                <button
                  type="button"
                  aria-label="Close"
                  className="mx-3 flex h-9 w-9 items-center justify-center rounded-full bg-white text-slateCustom transition hover:text-pinkCustom"
                  onClick={onClose}
                >
                  <X className="h-4 w-4" strokeWidth={4} />
                </button>
              </div>
            </div>

            <div className="relative flex flex-col">
              <div className="flex flex-col pl-8 text-lg">
                {blockOptions.map((opt) => (
                  <button
                    onClick={() => {
                      onAddBlock(opt.type);
                      onClose();
                    }}
                    className="relative mx-4 flex items-center justify-start gap-3 border-b border-gray-300 py-3 text-left last:border-none"
                    key={opt.label}
                  >
                    <span className="absolute top-3.5 -left-6">
                      {(() => {
                        const Icon = iconMap[opt.type] ?? PlusSVG;
                        return (
                          <Icon
                            className="h-5 w-5 text-pinkCustom"
                            svgColor="var(--color-pinkCustom)"
                          />
                        );
                      })()}
                    </span>
                    <span className="text-base text-slate-800">{opt.label}</span>
                  </button>
                ))}
              </div>
              <div
                className="flex w-5/6 gap-2 self-center"
                aria-label="Block actions"
              >
                <button
                  type="button"
                  className="bg-pinkCustom w-full rounded-md py-2 text-center text-lg text-white disabled:cursor-not-allowed disabled:opacity-60"
                  onClick={() => {
                    if (!canCloneBlocks || cloning) return;
                    onClose();
                    onOpenClone();
                  }}
                  disabled={!canCloneBlocks || cloning}
                  title={
                    canCloneBlocks
                      ? undefined
                      : "Cloning is unavailable in this context."
                  }
                >
                  {cloning ? "Cloning…" : "Clone Content"}
                </button>
                <button
                  type="button"
                  className="border-pinkCustom text-pinkCustom hover:bg-pinkCustom/10 w-full rounded-md border py-2 text-center text-lg font-semibold transition disabled:cursor-not-allowed disabled:opacity-60"
                  onClick={() => {
                    onPasteBlock();
                    onClose();
                  }}
                >
                  Paste block
                </button>
              </div>
            </div>
          </div>
        </OverlayWrapper>
      )}
    </AnimatePresence>
  );
}
